import { Check, Files, ImageOff } from 'lucide-react'
import { ComponentProps, useEffect, useRef, useState } from 'react'
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import { webchatClasses } from '../../../styles/classes'
import { TextBlock, type CommonBlockProps } from '../../../types'

type Props = TextBlock & CommonBlockProps
export const Text = ({ text }: Props) => {
  const {
    message: {
      blocks: { text: markdownStyles },
    },
  } = webchatClasses
  const [imageErrors, setImageErrors] = useState<Map<string, boolean>>(new Map())

  function checkIfImageHasError(imageUrl: string) {
    if (imageErrors.has(imageUrl)) {
      return
    }

    const img = new Image()
    img.src = imageUrl
    img.onerror = () => {
      setImageErrors((prev) => new Map(prev).set(imageUrl, true))
    }
    img.onload = () => {
      setImageErrors((prev) => new Map(prev).set(imageUrl, false))
    }
  }

  // Extract image URLs from markdown and check them in useEffect
  useEffect(() => {
    const imgRegex = /!\[.*?\]\((.*?)\)/g
    const urls: string[] = []
    let match

    while ((match = imgRegex.exec(text)) !== null) {
      const url = match[1]
      if (url && !urls.includes(url)) {
        urls.push(url)
      }
    }

    urls.forEach((imageUrl) => {
      checkIfImageHasError(imageUrl)
    })
  }, [text])

  const markdownComponents: ComponentProps<typeof ReactMarkdown>['components'] = {
    h1: ({ node, ...props }) => <h1 {...(props as ComponentProps<'h1'>)} {...markdownStyles.heading1} />,
    h2: ({ node, ...props }) => <h2 {...(props as ComponentProps<'h2'>)} {...markdownStyles.heading2} />,
    h3: ({ node, ...props }) => <h3 {...(props as ComponentProps<'h3'>)} {...markdownStyles.heading3} />,
    h4: ({ node, ...props }) => <h4 {...(props as ComponentProps<'h4'>)} {...markdownStyles.heading4} />,
    h5: 'h4',
    h6: 'h4',
    em: ({ node, ...props }) => <em {...(props as ComponentProps<'em'>)} {...markdownStyles.italic} />,
    strong: ({ node, ...props }) => <strong {...(props as ComponentProps<'strong'>)} {...markdownStyles.bold} />,
    p: ({ node, ...props }) => <p {...(props as ComponentProps<'p'>)} {...markdownStyles.text} />,
    hr: ({ node, ...props }) => <hr {...(props as ComponentProps<'hr'>)} {...markdownStyles.horizontalRule} />,
    a: ({ node, href, children, ...props }) => (
      <a
        {...(props as ComponentProps<'a'>)}
        {...markdownStyles.link}
        // Workaround to trigger link opening because of weird behavior causing the href to not work
        onMouseUp={(e) => {
          window.open(href, '_blank', 'noopener,noreferrer')
        }}
      >
        {children}
      </a>
    ),
    img: ({ node, ...props }) => {
      const imageUrl = props.src as string
      const hasError = imageErrors.get(imageUrl) || false

      return hasError ? (
        <div data-error={true} {...markdownStyles.image}>
          <ImageOff strokeWidth="1" />
        </div>
      ) : (
        <img {...(props as ComponentProps<'img'>)} {...markdownStyles.image} />
      )
    },
    ol: ({ node, ...props }) => <ol {...(props as ComponentProps<'ol'>)} {...markdownStyles.orderedList} />,
    ul: ({ node, ...props }) => <ul {...(props as ComponentProps<'ul'>)} {...markdownStyles.unorderedList} />,
    li: ({ node, ...props }) => <li {...(props as ComponentProps<'li'>)} {...markdownStyles.listItem} />,
    br: ({ node, ...props }) => <br {...(props as ComponentProps<'br'>)} {...markdownStyles.lineBreak} />,
    pre: ({ node, ...props }) => {
      const codeElement = node?.children[0] as ComponentProps<'pre'>
      const language = extractLanguage(codeElement?.className || '')

      const [codeCopied, setCodeCopied] = useState(false)
      const codeRef = useRef<HTMLPreElement>(null)

      const handleCodeCopy = () => {
        if (!codeRef.current) return

        const codeContent = codeRef.current.textContent || ''

        navigator.clipboard
          .writeText(codeContent)
          .then(() => {
            setCodeCopied(true)

            setTimeout(() => {
              setCodeCopied(false)
            }, 1500)
          })
          .catch((err) => {
            console.error('Failed to copy code to clipboard:', err)
          })
      }

      return (
        <div {...markdownStyles.codeBlockContainer}>
          <div {...markdownStyles.codeBlockTitle}>
            {language ? <span>{language}</span> : 'Code'}
            {codeCopied ? (
              <Check {...markdownStyles.codeBlockCopyIcon} />
            ) : (
              <Files {...markdownStyles.codeBlockCopyIcon} onClick={handleCodeCopy}></Files>
            )}
          </div>
          <pre ref={codeRef} {...(props as ComponentProps<'pre'>)} {...markdownStyles.codeBlock} />
        </div>
      )
    },
    input: ({ node, ...props }) => (
      <input {...(props as ComponentProps<'input'>)} {...markdownStyles.input} disabled={false} />
    ),
    code: ({ node, ...props }) => <code {...(props as ComponentProps<'code'>)} {...markdownStyles.code} />,
    table: ({ node, ...props }) => {
      return (
        <div {...markdownStyles.tableContainer}>
          <table {...(props as ComponentProps<'table'>)} {...markdownStyles.table} />
        </div>
      )
    },
    th: ({ node, ...props }) => <th {...(props as ComponentProps<'th'>)} {...markdownStyles.tableHeader} />,
    tr: ({ node, ...props }) => <tr {...(props as ComponentProps<'tr'>)} {...markdownStyles.tableRow} />,
    td: ({ node, ...props }) => <td {...(props as ComponentProps<'td'>)} {...markdownStyles.tableData} />,
  }

  return (
    <ReactMarkdown
      urlTransform={(url) => (url.startsWith('tel:') ? url : defaultUrlTransform(url))}
      components={markdownComponents}
      remarkPlugins={[remarkGfm, remarkBreaks]}
    >
      {text}
    </ReactMarkdown>
  )
}

const extractLanguage = (className: string | undefined) => {
  if (!className) return ''
  const match = className.match(/language-(\w+)/)
  return match ? match[1] : ''
}
